import { React, useState, useEffect } from 'react'
import { useLocation, useOutletContext, useNavigate } from 'react-router';
import { Stack, Row, Col, Alert } from 'react-bootstrap';
import Breadcrumbs from '../Components/Breadcrumbs';
import AcademyLoading from '../Components/AcademyLoading';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { analytics } from '../utils/firebase';
import { logEvent } from 'firebase/analytics';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function File(props) {
    const file_id = new URLSearchParams(useLocation().search).get('fid');
    const {routes, user, series, loading, seriesLoading} = useOutletContext();
    const navigate = useNavigate();
    const [selectedSeries, setSelectedSeries] = useState(null)
    const [file, setFile] = useState(null)
    const [fileDownloadUrl, setFileDownloadUrl] = useState(null)
    const [fileLoading, setFileLoading] = useState(false)
    const [fileError, setFileError] = useState(null)
    const [numPages, setNumPages] = useState(null)
    const [scale, setScale] = useState(1.0)
    const [currentPage, setCurrentPage] = useState(1)
    const [sidenavCollapsed, setSidenavCollapsed] = useState(false)

    // Update selectedSeries and file when series data becomes available
    useEffect(() => {
        if (series && series.length > 0 && file_id) {
            const foundSeries = series.find((s) => s.files && s.files.some((f) => f.uuid === file_id));
            const foundFile = foundSeries?.files.find((f) => f.uuid === file_id);
            setSelectedSeries(foundSeries);
            setFile(foundFile);

            // Track file page view
            if (foundSeries && foundFile) {
                logEvent(analytics, 'page_view', {
                    page_title: `Ukie's Academy - ${foundSeries.name} - ${foundFile.name}`,
                    page_location: window.location.href,
                    content_type: 'file',
                    series_id: foundSeries.uuid,
                    series_name: foundSeries.name,
                    file_id: foundFile.uuid,
                    file_name: foundFile.name,
                    file_type: foundFile.fileType
                });
            }
        }
    }, [series, file_id]);

    // Check access using the same logic as Series.js
    const access = selectedSeries ? 
        (selectedSeries.isFree && selectedSeries.freeRequiresSignup && user && user.optedIn) || 
        (selectedSeries.isFree && !selectedSeries.freeRequiresSignup) || 
        (!selectedSeries.isFree && selectedSeries.access)
        : false;

    // Redirect to series page if user doesn't have access (only after data is fully loaded)
    useEffect(() => {
        // Only check access after both series and user data are fully loaded
        if (!loading && !seriesLoading && selectedSeries && file) {
            // Track access check
            logEvent(analytics, 'file_access_check', {
                series_id: selectedSeries.uuid,
                series_name: selectedSeries.name,
                file_id: file.uuid,
                file_name: file.name,
                has_access: access,
                is_free: selectedSeries.isFree,
                requires_signup: selectedSeries.freeRequiresSignup
            });

            if (!access) {
                // Track access denied
                logEvent(analytics, 'file_access_denied', {
                    series_id: selectedSeries.uuid,
                    series_name: selectedSeries.name,
                    file_id: file.uuid,
                    file_name: file.name,
                    reason: selectedSeries.isFree ? 'signup_required' : 'purchase_required'
                });
                
            navigate(`/series?sid=${selectedSeries.uuid}`);
        }
        }
    }, [selectedSeries, file, access, navigate, loading, seriesLoading]);

    // Get Firebase storage download URL
    const fetchFileFromStorage = async (fileLink) => {
        if (!fileLink) return;
        
        setFileLoading(true);
        setFileError(null);
        
        // Track file loading start
        logEvent(analytics, 'file_load_start', {
            series_id: selectedSeries?.uuid,
            series_name: selectedSeries?.name,
            file_id: file?.uuid,
            file_name: file?.name,
            file_path: fileLink,
            file_type: file?.fileType
        });
        
        try {
            const storage = getStorage();
            // Remove leading slash if present since Firebase storage refs don't use leading slashes
            const cleanPath = fileLink.startsWith('/') ? fileLink.substring(1) : fileLink;
            const fileRef = ref(storage, cleanPath);
            
            // Get the download URL
            const downloadURL = await getDownloadURL(fileRef);
            
            setFileDownloadUrl(downloadURL);
            
            // Track successful file load
            logEvent(analytics, 'file_load_success', {
                series_id: selectedSeries?.uuid,
                series_name: selectedSeries?.name,
                file_id: file?.uuid,
                file_name: file?.name,
                file_type: file?.fileType
            });
            
            // Track file view
            logEvent(analytics, 'file_view', {
                series_id: selectedSeries?.uuid,
                series_name: selectedSeries?.name,
                file_id: file?.uuid,
                file_name: file?.name,
                file_type: file?.fileType,
                view_method: 'pdf.js'
            });
            
        } catch (error) {
            console.error('Error fetching file from storage:', error);
            
            // Track file load error
            logEvent(analytics, 'file_load_error', {
                series_id: selectedSeries?.uuid,
                series_name: selectedSeries?.name,
                file_id: file?.uuid,
                file_name: file?.name,
                error_message: error.message
            });
            
            setFileError('Fout bij het laden van het bestand. Probeer het opnieuw.');
        } finally {
            setFileLoading(false);
        }
    };

    // Fetch file when file data becomes available
    useEffect(() => {
        if (file && file.fileLink && access) {
            fetchFileFromStorage(file.fileLink);
        }
        
        // Reset download URL when file changes
        return () => {
            setFileDownloadUrl(null);
        };
    }, [file?.fileLink, access]);

    // PDF event handlers
    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setCurrentPage(1);
        
        // Track successful PDF load
        logEvent(analytics, 'pdf_load_success', {
            series_id: selectedSeries?.uuid,
            series_name: selectedSeries?.name,
            file_id: file?.uuid,
            file_name: file?.name,
            num_pages: numPages,
            file_type: file?.fileType
        });
    };

    const onDocumentLoadError = (error) => {
        console.error('Error loading PDF:', error);
        setFileError('Fout bij het laden van het PDF-bestand. Probeer het opnieuw.');
        
        // Track PDF load error
        logEvent(analytics, 'pdf_load_error', {
            series_id: selectedSeries?.uuid,
            series_name: selectedSeries?.name,
            file_id: file?.uuid,
            file_name: file?.name,
            error_message: error.message
        });
    };

    // Zoom functions
    const zoomIn = () => {
        setScale(prevScale => Math.min(prevScale + 0.25, 3.0)); // Max zoom 3x
        
        // Track zoom in
        logEvent(analytics, 'pdf_zoom_in', {
            series_id: selectedSeries?.uuid,
            file_id: file?.uuid,
            new_scale: Math.min(scale + 0.25, 3.0)
        });
    };

    const zoomOut = () => {
        setScale(prevScale => Math.max(prevScale - 0.25, 0.5)); // Min zoom 0.5x
        
        // Track zoom out
        logEvent(analytics, 'pdf_zoom_out', {
            series_id: selectedSeries?.uuid,
            file_id: file?.uuid,
            new_scale: Math.max(scale - 0.25, 0.5)
        });
    };

    const scrollToPage = (pageNum) => {
        const pageElement = document.getElementById(`page_${pageNum}`);
        if (pageElement) {
            pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setCurrentPage(pageNum);
            
            // Track page navigation
            logEvent(analytics, 'pdf_navigate_to_page', {
                series_id: selectedSeries?.uuid,
                file_id: file?.uuid,
                page_number: pageNum
            });
        }
    };

    const toggleSidenav = () => {
        setSidenavCollapsed(prev => !prev);
        
        // Track sidenav toggle
        logEvent(analytics, 'pdf_sidenav_toggle', {
            series_id: selectedSeries?.uuid,
            file_id: file?.uuid,
            collapsed: !sidenavCollapsed
        });
    };

    // Update page title when series and file are loaded
    useEffect(() => {
        if (selectedSeries && selectedSeries.name && file && file.name) {
            document.title = "Ukie's Academy - " + selectedSeries.name + " - " + file.name;
        }
        
        // Cleanup function to reset title when component unmounts
        return () => {
            document.title = "Ukie's Academy";
        };
    }, [selectedSeries, file]);

    // Add custom scrollbar styles
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            /* Webkit scrollbar styling for dark theme */
            #pdf-sidenav-container::-webkit-scrollbar,
            #pdf-viewer-container::-webkit-scrollbar {
                width: 8px;
            }
            
            #pdf-sidenav-container::-webkit-scrollbar-track {
                background: #28292a;
            }
            
            #pdf-viewer-container::-webkit-scrollbar-track {
                background: #282828;
            }
            
            #pdf-sidenav-container::-webkit-scrollbar-thumb,
            #pdf-viewer-container::-webkit-scrollbar-thumb {
                background: #6c757d;
                border-radius: 4px;
            }
            
            #pdf-sidenav-container::-webkit-scrollbar-thumb:hover,
            #pdf-viewer-container::-webkit-scrollbar-thumb:hover {
                background: #8ab4f7;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Intersection Observer to track current page while scrolling
    useEffect(() => {
        if (!numPages) return;

        let visiblePages = new Set();

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const pageId = entry.target.id;
                    const pageNumber = parseInt(pageId.replace('page_', ''));
                    
                    if (entry.isIntersecting) {
                        visiblePages.add(pageNumber);
                    } else {
                        visiblePages.delete(pageNumber);
                    }
                });

                // Update current page to the first visible page (topmost)
                if (visiblePages.size > 0) {
                    const topVisiblePage = Math.min(...visiblePages);
                    setCurrentPage(prevPage => {
                        if (prevPage !== topVisiblePage) {
                            // Track page change
                            logEvent(analytics, 'pdf_scroll_to_page', {
                                series_id: selectedSeries?.uuid,
                                file_id: file?.uuid,
                                from_page: prevPage,
                                to_page: topVisiblePage
                            });
                        }
                        return topVisiblePage;
                    });
                }
            },
            {
                root: document.getElementById('pdf-viewer-container'), // The PDF viewer container
                rootMargin: '-20% 0px -20% 0px', // Only trigger when page is substantially visible
                threshold: [0, 0.25, 0.5, 0.75, 1] // Multiple thresholds for better tracking
            }
        );

        // Small delay to ensure DOM elements are rendered
        const timeoutId = setTimeout(() => {
            for (let i = 1; i <= numPages; i++) {
                const pageElement = document.getElementById(`page_${i}`);
                if (pageElement) {
                    observer.observe(pageElement);
                }
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, [numPages]);

    // Auto-scroll sidenav to keep current page visible
    useEffect(() => {
        if (currentPage && numPages && !sidenavCollapsed) {
            const navContainer = document.getElementById('pdf-sidenav-container');
            const currentThumbnail = document.getElementById(`nav_thumb_${currentPage}`);
            
            if (navContainer && currentThumbnail) {
                const containerRect = navContainer.getBoundingClientRect();
                const thumbnailRect = currentThumbnail.getBoundingClientRect();
                
                // Check if thumbnail is out of view
                if (thumbnailRect.top < containerRect.top || thumbnailRect.bottom > containerRect.bottom) {
                    currentThumbnail.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }
        }
    }, [currentPage, numPages, sidenavCollapsed]);

    // Show loading while data is being fetched or series is not found yet
    if(loading || seriesLoading || fileLoading || !selectedSeries) {
        return(
            <AcademyLoading type='Bestand' />
        ) 
    }

    return (
        <Stack>
            {selectedSeries && (
                <div className="structure-fullwidth" style={{backgroundImage:'url('+selectedSeries.image+')',backgroundPosition:`${selectedSeries?.imagePositionX || 50}% ${selectedSeries?.imagePositionY || 50}%`}}>
                    <div className="structure header-banner structure-1 lg:structure-2 items-end" style={{alignItems:'flex-end',height:'500px'}}>
                        <div className="component component-text white text-shadow">
                            <div className="preheader-text white">Ukie's Academy</div>
                            <div className="header-text">{selectedSeries.name}</div>
                        </div>
                    </div>
                </div>
            )}
            <Breadcrumbs routes={routes} user={user} series={series} />
            <Row style={{padding:'2rem 0 0 0'}}>
                <div className='component component-text'>
                    <div className='preheader-text-small'>
                        {selectedSeries?.name || 'Loading...'}
                    </div>
                    <div className='header-text-small'>
                        {file?.name || 'Loading...'}
                    </div>
                </div>
            </Row>
            <Row style={{padding:'0 0 0 0'}}>
                <div className='component component-text'>
                    <div className='body-text' dangerouslySetInnerHTML={{__html:file?.description || ''}}>
                    </div>
                </div>
            </Row>
            {
                access && file
                ?
                    <Row style={{padding:'1rem 0rem'}}>
                        <Col>
                            {fileLoading && (
                                <div className="text-center" style={{padding: '2rem'}}>
                                    <AcademyLoading type='Bestand' />
                                </div>
                            )}
                            {fileError && (
                                <Alert variant='danger' style={{margin: '1rem 0'}}>
                                    {fileError}
                                </Alert>
                            )}
                            {fileDownloadUrl && !fileLoading && !fileError && (
                                <>
                                    {/* PDF Controls Bar */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: '15px',
                                        padding: '10px',
                                        backgroundColor: '#3c3c3c',
                                        color: '#fff',
                                        borderRadius: '0'
                                    }}>
                                        {/* Hamburger Menu */}
                                        <button
                                            onClick={toggleSidenav}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#fff',
                                                cursor: 'pointer',
                                                padding: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px'
                                            }}
                                            title={sidenavCollapsed ? 'Show thumbnails' : 'Hide thumbnails'}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                <line x1="2" y1="4.5" x2="16" y2="4.5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                                                <line x1="2" y1="9" x2="16" y2="9" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                                                <line x1="2" y1="13.5" x2="16" y2="13.5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                                            </svg>
                                        </button>

                                        {/* Center Controls */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '15px'
                                        }}>
                                            {/* Page Counter */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontSize: '14px',
                                                color: '#fff'
                                            }}>
                                                <span>
                                                    {currentPage} / {numPages || 0}
                                                </span>
                                            </div>

                                            {/* Separator */}
                                            <span style={{
                                                color: '#777777',
                                                fontSize: '16px',
                                                userSelect: 'none'
                                            }}>|</span>

                                        {/* Zoom Controls */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px'
                                        }}>
                                            <button 
                                                onClick={zoomOut}
                                                disabled={scale <= 0.5}
                                                className="btn btn-outline-secondary btn-sm"
                                                title="Zoom uit"
                                                style={{
                                                    color: '#fff',
                                                    border:'none',
                                                    boxShadow:'none',
                                                    borderRadius:'50%',
                                                    height:'30px',
                                                    width: '30px',
                                                    display:'flex',
                                                    alignItems:'center',
                                                    justifyContent:'center',
                                                }}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                    <line x1="2" y1="6" x2="10" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                                                </svg>
                                            </button>
                                            <div style={{
                                                minWidth: '50px',
                                                textAlign: 'center',
                                                fontSize: '14px',
                                                backgroundColor: '#1e1e1e',
                                                padding: '0.25rem',
                                                textAlign: 'center'
                                            }}>
                                                {Math.round(scale * 100)}%
                                            </div>
                                            <button 
                                                onClick={zoomIn}
                                                disabled={scale >= 3.0}
                                                className="btn btn-outline-secondary btn-sm"
                                                title="Zoom in"
                                                style={{
                                                    color: '#fff',
                                                    border:'none',
                                                    boxShadow:'none',
                                                    borderRadius:'50%',
                                                    height:'30px',
                                                    width: '30px',
                                                    display:'flex',
                                                    alignItems:'center',
                                                    justifyContent:'center',
                                                }}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                    <line x1="6" y1="2" x2="6" y2="10" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                                                    <line x1="2" y1="6" x2="10" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                        </div>

                                        {/* Right Spacer */}
                                        <div style={{ width: '34px' }}></div>
                                    </div>
                                    
                                    {/* PDF Viewer with Sidenav */}
                                    <div style={{ display: 'flex', gap: '0', height: '80vh' }}>
                                        {/* Side Navigation with Thumbnails */}
                                        {!sidenavCollapsed && (
                                        <div 
                                            id="pdf-sidenav-container"
                                            style={{
                                                width: '200px',
                                                height: '100%',
                                                overflowY: 'auto',
                                                borderRight: '2px solid #3c3c3c',
                                                borderRadius: '0',
                                                backgroundColor: '#28292a',
                                                color: '#fff',
                                                padding: '10px',
                                                scrollbarWidth: 'normal',
                                                scrollbarColor: '#8b8b8b #fcfcfc',
                                                transition: 'all 0.3s ease'
                                            }}>
                                            {numPages && Array.from(new Array(numPages), (el, index) => (
                                                <div 
                                                    key={`nav_page_${index + 1}`}
                                                    id={`nav_thumb_${index + 1}`}
                                                    style={{
                                                        margin: '8px 0',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        backgroundColor: '3c3c3c'
                                                    }}
                                                >
                                                    {/* Thumbnail */}
                                                    <div 
                                                        onClick={() => scrollToPage(index + 1)}
                                                        style={{
                                                            width: '140px',
                                                            height: '195px',
                                                            border: currentPage === index + 1 ? '5px solid #8ab4f7' : '5px solid transparent',
                                                            borderRadius: '0',
                                                            overflow: 'hidden',
                                                            backgroundColor: 'transparent',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            position: 'relative',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            boxShadow: 'none', //currentPage === index + 1 ? '0 2px 8px rgba(0,123,255,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
                                                            opacity: currentPage === index + 1 ? 1 : 0.5
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (currentPage !== index + 1) {
                                                                e.target.style.opacity = 1;
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (currentPage !== index + 1) {
                                                                e.target.style.opacity = currentPage === index + 1 ? 1 : 0.5;
                                                            }
                                                        }}
                                                    >
                                                        <Document
                                                            file={fileDownloadUrl}
                                                            onLoadError={() => {
                                                                // Silently handle thumbnail load errors
                                                            }}
                                                            loading={<div style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                backgroundColor: 'transparent',
                                                                fontSize: '10px',
                                                                color: '#666'
                                                            }}>
                                                                Laden...
                                                            </div>}
                                                        >
                                                            <Page
                                                                pageNumber={index + 1}
                                                                width={136}
                                                                renderTextLayer={false}
                                                                renderAnnotationLayer={false}
                                                                loading={<div style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    backgroundColor: 'transparent',
                                                                    fontSize: '10px',
                                                                    color: '#666'
                                                                }}>
                                                                    Laden...
                                                                </div>}
                                                                onLoadError={() => {
                                                                    // Silently handle page render errors for thumbnails
                                                                }}
                                                            />
                                                        </Document>
                                                    </div>
                                                    
                                                    {/* Page Number */}
                                                    <span style={{
                                                        fontSize: '12px',
                                                        fontWeight: 'normal',
                                                        color: '#fff',
                                                        textAlign: 'center',
                                                        opacity: 1,
                                                        marginBottom: '1rem',
                                                    }}>
                                                        {index + 1}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        )}

                                        {/* Main PDF Viewer */}
                                        <div 
                                            id="pdf-viewer-container"
                                            style={{
                                                flex: 1,
                                                height: '100%',
                                                overflowY: 'scroll',
                                                borderRadius: '0',
                                                backgroundColor: '#282828',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                padding: '20px',
                                                scrollbarWidth: 'normal',
                                                scrollbarColor: '#8b8b8b #fcfcfc'
                                            }}>
                                            <Document
                                                file={fileDownloadUrl}
                                                onLoadSuccess={onDocumentLoadSuccess}
                                                onLoadError={onDocumentLoadError}
                                                loading={<div style={{textAlign: 'center', padding: '2rem'}}><AcademyLoading type='PDF' /></div>}
                                            >
                                                {numPages && Array.from(new Array(numPages), (el, index) => (
                                                    <div 
                                                        key={`page_${index + 1}`} 
                                                        id={`page_${index + 1}`}
                                                        style={{
                                                            marginBottom: '20px',
                                                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                                            borderRadius: '4px',
                                                            overflow: 'hidden',
                                                            backgroundColor: 'white'
                                                        }}
                                                    >
                                                        <Page 
                                                            pageNumber={index + 1}
                                                            width={595 * scale}
                                                            loading={<div style={{padding: '1rem', textAlign: 'center'}}><AcademyLoading type='Pagina' /></div>}
                                                        />
                                                    </div>
                                                ))}
                                            </Document>
                                        </div>
                                    </div>
                                </>
                            )}
                        </Col>
                    </Row>
                :
                    <Row style={{padding:'1rem',marginBottom:'-2rem'}}>
                        <a href='https://www.ukies.nl/account/login' target='_parent' style={{padding:'0'}}>
                            <Alert variant='warning'>Je bent niet ingelogd, <b>log in</b> om toegang te krijgen tot jouw cursussen</Alert>     
                        </a>
                    </Row>
            }
        </Stack>
    );
}